import { createFileRoute, useNavigate, Outlet, useMatch } from "@tanstack/react-router";

import { ProjectGroupLanding } from "../components/projects/ProjectGroupLanding";
import { isProjectTab, type ProjectTab } from "../components/projects/CmmdProjectTabs";
import { parseTaskSearchParam } from "../components/projects/taskSearchParam";

/**
 * A project is addressed by the checkout GROUP it belongs to — the repository —
 * so the same URL opens the same project from a laptop, a desktop or a sprite.
 *
 * The param is deliberately named `projectKey` rather than an id: it accepts a
 * group key, a Forge project id, or a CMMD board id, and canonicalises on
 * arrival. Every link written before this change carries one of the latter two,
 * including ones other agents wrote into CMMD task cards.
 */
export const Route = createFileRoute("/_chat/project/$projectKey")({
  validateSearch: (search: Record<string, unknown>): { task?: string; tab?: ProjectTab } => {
    const task = parseTaskSearchParam(search["task"]);
    const tab = search["tab"];
    return {
      ...(task !== undefined ? { task } : {}),
      ...(isProjectTab(tab) ? { tab } : {}),
    };
  },
  component: ProjectKeyRouteView,
});

function ProjectKeyRouteView() {
  const navigate = useNavigate();
  const { projectKey } = Route.useParams();
  const search = Route.useSearch();
  const boardMatch = useMatch({
    from: "/_chat/project/$projectKey/tasks/$cmmdProjectId",
    shouldThrow: false,
  });
  if (boardMatch) return <Outlet />;

  return (
    <ProjectGroupLanding
      projectKey={projectKey}
      tab={search.tab}
      onNavigate={(to) => {
        void navigate({ ...to, search, replace: true });
      }}
      onLeave={() => {
        void navigate({ to: "/", replace: true });
      }}
    />
  );
}
